import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { apiService } from "../api/api";
import { UserRoles } from "../config/routes";
import {
  DEV_PREVIEW_ROLE_EVENT,
  getDevPreviewEmail,
  getDevPreviewRole,
  parseUserTools,
  resolveUserRoles,
} from "../utils/userAccess";

export interface UserAccess extends UserRoles {
  uid: string | null;
  /** Email used for "my" data; a channel manager's email while previewing on localhost. */
  email: string | null;
  /** The signed-in user's own email. */
  realEmail: string | null;
  /** Set while previewing as another channel manager (localhost only). */
  previewEmail: string | null;
  displayName: string | null;
  isAdmin: boolean;
}

let usersPromise: Promise<Map<string, UserRoles>> | null = null;

/** Role + tools of every Orion user, keyed by lower-cased email (cached; preview only). */
const loadUserRoles = (): Promise<Map<string, UserRoles>> => {
  if (!usersPromise) {
    usersPromise = apiService.users
      .getAllUsers("all")
      .then((res: any) => {
        const users: any[] = Array.isArray(res?.data) ? res.data : [];
        return new Map(
          users
            .filter((u) => typeof u.email === "string")
            .map((u) => [
              u.email.toLowerCase(),
              { role: u.role || "user", tools: u.active === false ? [] : parseUserTools(u.tools) },
            ])
        );
      })
      .catch((e) => {
        usersPromise = null;
        throw e;
      });
  }
  return usersPromise;
};

/**
 * Role + tools of the previewed channel manager, so the page shows only what
 * they would see. `undefined` while loading; managers without an Orion account
 * get the default user role with no tools.
 */
const usePreviewRoles = (previewEmail: string | null): UserRoles | null | undefined => {
  const [roles, setRoles] = useState<UserRoles | null | undefined>(previewEmail ? undefined : null);

  useEffect(() => {
    if (!previewEmail) {
      setRoles(null);
      return;
    }
    let cancelled = false;
    setRoles(undefined);
    loadUserRoles()
      .then((byEmail) => {
        if (!cancelled) setRoles(byEmail.get(previewEmail.toLowerCase()) ?? { role: "user", tools: [] });
      })
      .catch((e) => {
        console.error("Failed to load the previewed user's tools", e);
        if (!cancelled) setRoles({ role: "user", tools: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [previewEmail]);

  return roles;
};

/** Signed-in user's identity plus resolved role/tools (incl. dev overrides). */
export const useUserAccess = (): UserAccess => {
  const user = useSelector((state: any) => state.googleAuth.user);
  const [previewVersion, setPreviewVersion] = useState(0);

  useEffect(() => {
    const onChange = () => setPreviewVersion((v) => v + 1);
    window.addEventListener(DEV_PREVIEW_ROLE_EVENT, onChange);
    return () => window.removeEventListener(DEV_PREVIEW_ROLE_EVENT, onChange);
  }, []);

  // previewVersion re-reads the preview email after the dev toggle changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const previewEmail = useMemo(() => getDevPreviewEmail(user?.email), [user, previewVersion]);
  const previewRoles = usePreviewRoles(previewEmail);

  return useMemo(() => {
    let roles = resolveUserRoles(user);
    if (previewEmail) {
      // Use the previewed person's tools; the "View as" role buttons still override their role.
      const base = previewRoles ?? { role: "user", tools: [] };
      roles = { role: getDevPreviewRole(user?.email) ?? base.role, tools: base.tools };
    }
    return {
      ...roles,
      uid: user?.uid ?? null,
      email: previewEmail ?? user?.email ?? null,
      realEmail: user?.email ?? null,
      previewEmail,
      displayName: user?.displayName ?? null,
      isAdmin: roles.role === "admin",
    };
    // previewVersion re-resolves roles after the dev preview toggle changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, previewVersion, previewEmail, previewRoles]);
};
